import {Component, Input, OnInit} from '@angular/core';
import {CommandService} from '@app/services/command.service';
import {ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {AppService} from '@app/services/app.service';
import {Store} from '@ngxs/store';
import {AccessType} from '@app/modules/shared/data/constants';
import {WrtcService} from '@app/modules/wrtc/services/wrtc.service';
import {ApplauseLevel, CoreService} from '@app/services/core.service';
import {AssignmentService} from '@app/modules/whiteboard/services/assignment.service';
import {RemoteParticipantInfo} from '@app/models/remote-participant-info';
import {
  RemoteUserAction,
  RemoteUserAdditionalInfoStateModel,
  RemoteUserInfoStateModel,
  RemoteUsersInfoState
} from '@app/state/remote-users.state';
import {WhiteboardService} from '@app/modules/whiteboard/services/whiteboard.service';
import {Whiteboard, WhiteboardState} from '@app/state/whiteboard.state';
import {AssignmentState, AssignmentStateModel} from '@app/state/assignment.state';
import {LocalUser, LocalUserState} from '@app/state/local-user.state';
import {WebPortalMessagingService} from '@app/services/web-portal-messaging.service';

@Component({
  selector: 'app-user-action-panel',
  templateUrl: './user-action-panel.component.html',
  styleUrls: ['./user-action-panel.component.css']
})
export class UserActionPanelComponent implements OnInit {
  @Input() public remoteUser: RemoteParticipantInfo;
  @Input() public userIndex: number;
  @Input() public studentsCount: number;
  @Input() public isOpenedFromList: boolean;

  private remoteUsers: { [key: string]: RemoteUserAdditionalInfoStateModel };
  public localUser: LocalUser;

  public classroomNumber: number;
  public status = {
    isPopOverOpen:false,
    isOpenRedirect: false,
    isOpenFrameRate: false,
    isOpenVideoResolution: false,
    isOpenUserActions: false,
    isOpenLastHomework: false
  };
  public showFrameRateAndVideoResolutionChangeButtons = false;
  public classroom: ClassroomStateModel;
  public whiteboard: Whiteboard;
  public assignment: AssignmentStateModel;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public AccessType = AccessType;

  constructor(
    private store: Store,
    private appService: AppService,
    private coreService: CoreService,
    private wrtcService: WrtcService,
    private whiteboardService: WhiteboardService,
    private assignmentService: AssignmentService,
    private webPortalMessagingService: WebPortalMessagingService
  ) {
    this.listenStoreChanges();
  }

  public ngOnInit(): void {}

  private listenStoreChanges(): void {
    this.store.select(RemoteUsersInfoState).subscribe(({usersInfoById}: RemoteUserInfoStateModel) => {
      this.remoteUsers = usersInfoById;
    });
    this.store.select(ClassroomState).subscribe((classroom: ClassroomStateModel) => {
      this.classroom = classroom;
    });
    this.store.select(WhiteboardState).subscribe((whiteboard: Whiteboard) => {
      this.whiteboard = whiteboard;
    });
    this.store.select(AssignmentState).subscribe((assignment: AssignmentStateModel) => {
      this.assignment = assignment;
    });
    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });
  }

  public redirectStudent(): void {
    if (this.classroomNumber > 99 && this.classroomNumber < 10000) {
      this.status.isOpenRedirect = true;
      this.coreService.sendRedirectStudent(this.classroomNumber, this.remoteUser.id).subscribe();
    }
  }

  public setFrameRate(rate): void {
    this.wrtcService.sendFrameRate(rate, this.remoteUser.id).subscribe();
  }

  public setResolution(resolution): void {
    this.wrtcService.sendResolution(this.remoteUser.id, resolution).subscribe();
  }

  public reloadStudent(): void {
    this.wrtcService.sendReload(this.remoteUser.id).subscribe();
  }

  public expelStudent(): void {
    this.coreService.sendExpelStudent(this.remoteUser.id).subscribe();
  }

  public showReport(): void {
    this.appService.getGroupLastAssignmentId(this.remoteUser.id, false).subscribe((response: any) => {
      if (response.message === '') {
        return;
      }
      const assignmentId: number = JSON.parse(response.message).lastAssignmentId;
      if (!isNaN(assignmentId) && assignmentId > 0) {
        this.assignmentService.sendLastAssignmentReport(assignmentId).subscribe();
      }
    });
  }
  public removeSticker(): void {
    this.coreService.sendRemoveSticker(this.remoteUser.id).subscribe();
  }

  public toggleWhiteboardSharing(): void {
    this.coreService.toggleWhiteboardsSharingWith(this.remoteUser.id);
  }

  public toggleAssignmentSwitchAccessWith(): void {
    this.coreService.toggleAssignmentSwitchAccessWith(this.remoteUser.id);
  }

  public toggleAudioMute(dropQuiteMode: boolean = true): void {
    if (this.localUser.accessType === AccessType.OBSERVER) {
      return;
    }
    if (dropQuiteMode && this.classroom.isClassroomMuted) {
      const usersToMute: { [key: number]: RemoteUserAdditionalInfoStateModel } = Object.assign({}, this.remoteUsers);
      delete usersToMute[this.remoteUser.id];

      this.wrtcService.sendUsersAudioMute(
        Object.keys(usersToMute)
          .map((id: string) => parseInt(id))
      ).subscribe(() => {
        this.wrtcService.sendMuteRoom(false, false).subscribe(() => {
          if (this.remoteUser.additionalInfo.audio.isMuted) {
            this.wrtcService.sendAudioUnmute(this.remoteUser.id).subscribe();
          } else {
            this.wrtcService.sendAudioMute(this.remoteUser.id).subscribe();
          }
        });
      });
    } else {
      if (this.remoteUser.additionalInfo.audio.isMuted) {
        this.wrtcService.sendAudioUnmute(this.remoteUser.id).subscribe();
      } else {
        this.wrtcService.sendAudioMute(this.remoteUser.id).subscribe();
      }
    }
  }

  public toggleVideoMute(): void {
    if (this.remoteUser.additionalInfo.video.isMuted) {
      this.wrtcService.sendVideoUnmute(this.remoteUser.id).subscribe();
    } else {
      this.wrtcService.sendVideoMute(this.remoteUser.id).subscribe();
    }
    this.store.dispatch(new RemoteUserAction.SetUserVideoMute(this.remoteUser.id, !this.remoteUser.additionalInfo.video.isMuted));
  }

  public studentVoiceChangeHandler(): void {
    this.wrtcService.setParticipantVoiceLevel(this.remoteUser.id, this.remoteUser.additionalInfo.audio.voiceLevel);

  }

  public sendStudentVoiceChangedEvent(): void {
    this.store.dispatch(new RemoteUserAction.SetUserVoiceLevel(this.remoteUser.additionalInfo.audio.voiceLevel, this.remoteUser.id));
    this.wrtcService.sendVoiceLevel(this.remoteUser.additionalInfo.audio.voiceLevel, this.remoteUser.id).subscribe();
  }

  public playApplauseAudio(): void {
    this.coreService.applause(ApplauseLevel.INDIVIDUAL, this.remoteUser.id);
    this.coreService.sendIndividualApplause(this.remoteUser.id).subscribe();
  }

  public isApplauseSoundPlaying(): boolean {
    return this.coreService.applauseAudio.individual.isPlaying && this.coreService.applauseAudio.individual.id === this.remoteUser.id;
  }
}
